import { differenceInMonths, format } from 'date-fns'

export function formatDate(dateInput: Date | string | undefined): string {
    if (!dateInput) return 'present'
    const date = new Date(dateInput)
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
    }).format(date)
}

export function formatDateAllInfo(
    dateInput: Date | string | undefined,
): string {
    if (!dateInput) return 'N/A'
    const date = new Date(dateInput)
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    }).format(date)
}

export function formatDuration(
    startDate?: Date | string,
    endDate?: Date | string,
): { fromTo: string; duration: string } {
    if (!startDate)
        return {
            fromTo: 'N/A',
            duration: '',
        }

    const start = new Date(startDate)
    const isEndDateProvided = Boolean(endDate)
    const end = isEndDateProvided ? new Date(endDate!) : new Date() // Use today's date if endDate is falsey

    const startFormatted = format(start, 'MMM yyyy')
    const endFormatted = isEndDateProvided ? format(end, 'MMM yyyy') : 'present' // Use "present" if endDate is falsey

    const totalMonths = differenceInMonths(end, start) + 1 // Include start month
    const years = Math.floor(totalMonths / 12)
    const months = totalMonths % 12

    let duration = ''
    if (years > 0) {
        duration += `${years} year${years > 1 ? 's' : ''}`
    }
    if (months > 0) {
        duration += `${duration ? ', ' : ''}${months} month${
            months > 1 ? 's' : ''
        }`
    }
    // Deal with inconsistent date ranges that come from the imported data
    if (duration === '') {
        duration = 'N/A'
    }

    return {
        fromTo: `${startFormatted} - ${endFormatted}`,
        duration: duration,
    }
}

export function formatDurationMs(value: number | null | undefined): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return '—'
    }

    const totalMs = Math.max(0, value)
    const MS_IN_SECOND = 1000
    const MS_IN_MINUTE = 60 * MS_IN_SECOND
    const MS_IN_HOUR = 60 * MS_IN_MINUTE
    const MS_IN_DAY = 24 * MS_IN_HOUR

    let remaining = totalMs

    let days = Math.floor(remaining / MS_IN_DAY)
    remaining -= days * MS_IN_DAY

    let hours = Math.floor(remaining / MS_IN_HOUR)
    remaining -= hours * MS_IN_HOUR

    let minutes = Math.floor(remaining / MS_IN_MINUTE)
    remaining -= minutes * MS_IN_MINUTE

    const seconds = remaining / MS_IN_SECOND
    let secondsRounded = Math.round(seconds * 100) / 100

    if (secondsRounded >= 60) {
        secondsRounded -= 60
        minutes += 1
    }

    if (minutes >= 60) {
        const extraHours = Math.floor(minutes / 60)
        minutes %= 60
        hours += extraHours
    }

    if (hours >= 24) {
        const extraDays = Math.floor(hours / 24)
        hours %= 24
        days += extraDays
    }

    const parts: string[] = []
    if (days) parts.push(`${days}d`)
    if (hours) parts.push(`${hours}h`)
    if (minutes) parts.push(`${minutes}m`)

    const secondsStr = Number.isInteger(secondsRounded)
        ? `${secondsRounded}`
        : secondsRounded.toFixed(2)

    if (parts.length === 0 && secondsRounded === 0) {
        return '0s'
    }

    parts.push(`${secondsStr}s`)

    return parts.join(' ')
}

export function formatRelativeTime(
    dateInput: Date | string | number | undefined,
    nowInput: Date | number = new Date(),
): string {
    if (!dateInput) return ''

    const date = new Date(dateInput)
    if (Number.isNaN(date.getTime())) return ''
    const now = nowInput instanceof Date ? nowInput : new Date(nowInput)
    const diffMs = Math.max(0, now.getTime() - date.getTime())

    const seconds = Math.floor(diffMs / 1000)
    if (seconds < 60) return 'just now'

    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return minutes === 1 ? '1 min ago' : `${minutes} mins ago`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`

    const days = Math.floor(hours / 24)
    if (days < 2) return 'yesterday'
    if (days < 7) return `${days} days ago`

    if (days < 30) {
        const weeks = Math.floor(days / 7)
        return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
    }

    if (days < 365) {
        const months = Math.floor(days / 30) || 1
        const cappedMonths = Math.min(months, 11)
        return cappedMonths === 1 ? '1 month ago' : `${cappedMonths} months ago`
    }

    const years = Math.floor(days / 365)
    return years === 1 ? '1 year ago' : `${years} years ago`
}
